import {FC, useReducer} from 'react'
import {gql} from '@apollo/client'
import {useHistory} from 'react-router-dom'
import {useMutation} from 'hooks/graphql'
import {useNotify} from 'hooks/useNotify'
import {CommentModal} from 'components/modals/CommentModal'
import {
  APPLICATION_TMP_DATA_NAME as CHILDCARE_TMP_DATA_NAME,
  schema as childcareTmpDataSchema,
} from 'reportForms/R507Report'
import {ProcedureComment} from '../query'
import {decamelizeComments} from '../decamelizeComments'

const titleMap: Record<string, string> = {
  enroll: '入社の手続き',
  add_dependents: '家族の変更',
  remove_dependents: '家族の変更',
  change_name: '氏名の変更',
  change_address: '住所の変更',
  start_maternity: '産休の手続き',
  extend_maternity: '産休の手続き',
  start_childcare: '育休開始の手続き',
  extend_childcare: '育休延長の手続き',
  finish_childcare: '育休終了の手続き',
  parental_leave: '出生時育休の手続き',
  parental_leave_support_allowance: '出生後休業支援給付金の手続き',
}

const APPLY_PROCEDURE_STATUS = gql`
  mutation ApplyProcedureStatus($input: ApplyProcedureStatusInput!, $isChildcare: Boolean!) {
    applyProcedureStatus(input: $input) {
      procedureStatus {
        id
        childcareApplicationTmp: procedureTmpDatum(type: application, name: "${CHILDCARE_TMP_DATA_NAME}") @include(if: $isChildcare) {
          id
          type
          name
          data
        }
      }
      actionType
    }
  }
`

interface ApplyProcedureStatusQueryVariables {
  input: {
    procedureStatusId: string
    comment: string
  }
  isChildcare: boolean
}

interface ApplyProcedureStatusQueryResult {
  applyProcedureStatus: {
    procedureStatus: {
      id: string
      childcareApplicationTmp?: {
        id: string
        type: string
        name: string
        data: unknown
      }
    }
    actionType: string
  }
}

interface ApplyProps {
  procedureStatusId: string
  procedureType: string
  comments: ProcedureComment[]
  onCancel: () => Promise<void>
}

export const Apply: FC<ApplyProps> = ({procedureStatusId, procedureType, comments, onCancel}) => {
  const history = useHistory()
  const notify = useNotify()

  // submit/cancelした後のみcloseできるようにするためのreducer
  const [{isOpen}, dispatch] = useReducer(
    (state: {isOpen: boolean; confirmed: boolean}, action: 'confirm' | 'close') => {
      switch (action) {
        case 'confirm':
          return {...state, confirmed: true}
        case 'close':
          return state.confirmed ? {...state, isOpen: false} : state
        default:
          return state
      }
    },
    {isOpen: true, confirmed: false}
  )

  const [applyProcedureStatus] = useMutation<ApplyProcedureStatusQueryResult, ApplyProcedureStatusQueryVariables>(
    APPLY_PROCEDURE_STATUS
  )
  const handleSubmit = async (comment: string) => {
    const isChildcare = ['start_childcare', 'extend_childcare', 'finish_childcare', 'parental_leave'].includes(
      procedureType
    )
    const {data} = await applyProcedureStatus({
      variables: {
        input: {
          procedureStatusId,
          comment,
        },
        isChildcare,
      },
    })
    dispatch('confirm')
    notify('申請しました', 'success')

    const procedureStatus = data?.applyProcedureStatus.procedureStatus
    const actionType = data?.applyProcedureStatus.actionType

    // 育休手続きの場合「社会保険の被扶養者に追加する」チェックの判定が必要 (差し戻し後の再申請の場合は判定しない)
    if (actionType !== 'revised' && isChildcare) {
      const validated = await childcareTmpDataSchema.validate(procedureStatus?.childcareApplicationTmp?.data, {
        strict: true,
      })
      // 「社会保険の被扶養者に追加する」チェックがある場合は扶養追加の手続きに遷移
      if (validated?.childcareChildDatum.addSupport) {
        history.push(`/mypage/add_dependents/employee_input?childcare_procedure_status_id=${procedureStatusId}`)
        return
      }
    }

    history.push('/dashboard')
  }

  const handleCancel = async () => {
    await onCancel()
    dispatch('confirm')
  }

  return (
    <CommentModal
      title={titleMap[procedureType] || 'その他申請'}
      onSubmit={({comment}) => handleSubmit(comment)}
      onCancel={handleCancel}
      isOpen={isOpen}
      onClose={() => dispatch('close')}
      comments={decamelizeComments(comments)}
    />
  )
}
